/**
 * =============================================================================
 * WIDGET DEMO PAGE
 * =============================================================================
 * 
 * Demo page for the LGBT Resources Widget component.
 * Now includes integrated slider to switch between Widget and Map demos.
 * 
 * Features:
 * - Live widget preview with theme selection
 * - Integrated Resource List / Resource Map slider
 * - API key generation for embedding
 * - Youth Safe Mode toggle for testing
 * - Embedded mode support for iframe integration
 * - Responsive design with wave backgrounds
 * 
 * =============================================================================
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './WidgetDemo.css';
import LGBTResourcesWidget from './components/LGBTResourcesWidget';
import ApiKeyGenerator from './components/ApiKeyGenerator';
import ResourcesNav from './components/ResourcesNav';
import StartPageBackground from './assests/Start_Page.png';
import WhiteWaveBackground from './assests/White_Wave.png';

// =============================================================================
// MAIN COMPONENT
// =============================================================================

function WidgetDemo() {
  
  // ===========================================================================
  // URL PARAMETER PARSING
  // ===========================================================================
  
  const urlParams = new URLSearchParams(window.location.search);
  const embeddedParam = urlParams.get('embedded');
  const isEmbedded = embeddedParam === 'true' || embeddedParam === 'panel';
  const youthSafe = urlParams.get('youthSafe') === 'true';
  const theme = urlParams.get('theme') || 'purple';
  const hideFooter = urlParams.get('hideFooter') === 'true';
  const apiKey = urlParams.get('apiKey') || process.env.REACT_APP_WIDGET_API_KEY;
  
  // ===========================================================================
  // HOOKS
  // ===========================================================================
  
  const navigate = useNavigate();
  
  // ===========================================================================
  // STATE MANAGEMENT
  // ===========================================================================
  
  const [testTheme, setTestTheme] = useState('purple');
  const [testYouthSafe, setTestYouthSafe] = useState(false);
  
  // ===========================================================================
  // THEME CONFIGURATION
  // ===========================================================================
  
  const colorThemes = [
    { id: 'purple', name: 'Standard', preview: 'linear-gradient(135deg, #5CB85C 0%, #491994 100%)' },
    { id: 'inverted', name: 'Inverted', preview: 'linear-gradient(135deg, #af8bef 0%, #7DCD7D 100%)' },
    { id: 'monochrome', name: 'Monochrome', preview: 'linear-gradient(135deg, #666666 0%, #333333 100%)' },
    { id: 'ocean', name: 'Ocean Blue', preview: 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)' }
  ];
  
  // ===========================================================================
  // EFFECTS
  // ===========================================================================
  
  React.useEffect(() => {
    if (isEmbedded) {
      document.body.classList.add('embedded');
      document.documentElement.style.setProperty('--widget-theme', theme);
    } else {
      document.documentElement.style.setProperty('--start-page-bg-image', `url(${StartPageBackground})`);
      document.documentElement.style.setProperty('--white-wave-bg-image', `url(${WhiteWaveBackground})`);
      
      if (hideFooter) {
        document.body.classList.add('hide-footer');
      }
    }
    
    return () => {
      document.body.classList.remove('embedded');
      document.body.classList.remove('hide-footer');
      document.documentElement.style.removeProperty('--start-page-bg-image');
      document.documentElement.style.removeProperty('--white-wave-bg-image');
    };
  }, [isEmbedded, theme, hideFooter]);
  
  // ===========================================================================
  // EMBEDDED MODE RENDER
  // ===========================================================================
  
  if (isEmbedded) {
    return (
      <div style={{ 
        width: '100%', 
        height: '100vh', 
        background: '#491994',
        backgroundImage: `
          radial-gradient(circle at 20% 50%, rgba(175, 139, 239, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(92, 184, 92, 0.2) 0%, transparent 50%),
          radial-gradient(circle at 40% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)
        `,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ width: '100%', height: '100%', position: 'relative' }}>
          <LGBTResourcesWidget 
            defaultOpen={true} 
            hideButton={true} 
            youthSafe={youthSafe} 
            theme={theme}
            apiKey={apiKey}
          />
        </div>
      </div>
    );
  }
  
  // ===========================================================================
  // FULL DEMO PAGE RENDER
  // ===========================================================================
  
  return (
    <>
      {/* Fixed Background Container */}
      <div className="widget-fixed-background-container"></div>
      
      {/* Navigation Header */}
      <ResourcesNav />
      
      {/* Single Unified Section - All content in one wrapper for unified scrolling */}
      <div className="bottom-section-wrapper">
        
        {/* Hero Section - Intro Text */}
        <div className="widget-hero-section">
          <p className="widget-hero-tagline">ADD THE LARGEST LGBTQ+ RESOURCE DATABASE TO YOUR SITE</p>
          <h1 className="widget-hero-title">LGBT Near Me Website Widget</h1>
          <p className="widget-hero-text">
            The <a href="https://www.lgbthotline.org" target="_blank" rel="noopener noreferrer" className="widget-hero-link">LGBT National Help Center</a> is excited to invite community organizations in adding a <strong><u>free</u></strong> custom search tool that can be embedded on any website to encourage community connection and help find critically needed resources, LGBTQ+ owned businesses, social and support groups and much more!
          </p>
          <p className="widget-hero-text">
            With over 21,000 resources, LGBT Near Me is the largest resource database of its kind, and is constantly being updated and maintained by skilled LGBTQ+ community members to be up to date and accurate.
          </p>
          <p className="widget-hero-text">
            Designed to display either as a Resource List or a Resource Map, the LGBT Near Me Widget can be personalized to your website and to your community member's age, offering an all ages results or as Youth specific.
          </p>
        </div>
        
        {/* Feature Cards - How It Works and Features */}
        <div className="App-header">
          <div className="info-section">
            {/* How It Works Card */}
            <div className="feature-card">
              <h2>How It Works</h2>
              <ol className="how-it-works-list">
                <li>Request your API key below</li>
                <li>Copy the embed code</li>
                <li>Paste into your website</li>
                <li>Once approved visitors can find local resources through your website</li>
              </ol>
            </div>
            
            {/* Features Card */}
            <div className="feature-card">
              <h2>Features</h2>
              <ul>
                <li>Search by ZIP/Postal Code and radius</li>
                <li>Filter by search category</li>
                <li><strong><u>Free</u></strong> to use</li>
                <li>Youth-safe mode available</li>
                <li>Reliably and constantly updated by LGBT National Help Center resource staff</li>
              </ul>
            </div>
          </div>
        </div>
        
        {/* Choose Between Section - Slider Container */}
        <div className="choose-between-section">
          <div className="choose-between-card">
            <h3>Users can choose Resource List or Resource Map Display</h3>
            <div className="resource-type-slider">
              <button 
                className="resource-type-btn active"
                onClick={() => {}}
              >
                <span className="btn-label">Resource List</span>
                <span className="btn-subtitle">Floating Button</span>
              </button>
              <button 
                className="resource-type-btn"
                onClick={() => navigate('/map')}
              >
                <span className="btn-label">Resource Map</span>
                <span className="btn-subtitle">Static Iframe</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Testing Controls Section - Matches MapDemo style */}
        <div className="widget-testing-controls">
          <div className="testing-slider-container">
            {/* Header */}
            <div className="testing-slider-header">
              <h3>Live Demo Mode</h3>
              <span className="testing-slider-badge">Demo Only</span>
            </div>
            <p className="testing-slider-description">
              Test different modes and colour themes before generating your API key
            </p>
            
            {/* Content Mode Toggle - Adult / Youth Safe */}
            <div className="testing-section">
              <div className="testing-section-label">Content Mode</div>
              <div className="testing-mode-slider">
                {/* Adult Mode Button */}
                <button
                  className={`testing-mode-btn ${!testYouthSafe ? 'active' : ''}`}
                  onClick={() => setTestYouthSafe(false)}
                >
                  <span className="testing-mode-icon">👥</span>
                  <span className="testing-mode-label">Adult Mode</span>
                </button>
                
                {/* Sliding Indicator */}
                <div className="testing-mode-indicator" style={{
                  transform: testYouthSafe ? 'translateX(100%)' : 'translateX(0)'
                }}></div>
                
                {/* Youth Safe Mode Button */}
                <button
                  className={`testing-mode-btn ${testYouthSafe ? 'active' : ''}`}
                  onClick={() => setTestYouthSafe(true)}
                >
                  <span className="testing-mode-icon">🛡️</span>
                  <span className="testing-mode-label">Youth Safe</span>
                </button>
              </div>
            </div>
            
            {/* Colour Theme Selector */}
            <div className="testing-section">
              <div className="testing-section-label">Colour Theme</div>
              <div className="testing-theme-grid">
                {colorThemes.map(themeOption => (
                  <button
                    key={themeOption.id}
                    className={`testing-theme-btn ${testTheme === themeOption.id ? 'active' : ''}`}
                    onClick={() => setTestTheme(themeOption.id)}
                  >
                    <div 
                      className="testing-theme-preview" 
                      style={{ background: themeOption.preview }}
                    />
                    <span className="testing-theme-name">{themeOption.name}</span>
                    {testTheme === themeOption.id && (
                      <span className="testing-theme-check">✔</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Current Selection Display */}
            <div className="testing-mode-info">
              <div className="testing-info-combined">
                <div className="testing-info-row">
                  <span className="testing-info-label">Mode:</span>
                  <span className={`testing-info-value ${testYouthSafe ? 'youth' : 'adult'}`}>
                    {testYouthSafe ? '🛡️ Youth Safe' : '👥 Adult Mode'}
                  </span>
                </div>
                <div className="testing-info-row">
                  <span className="testing-info-label">Theme:</span>
                  <span className="testing-info-value theme">
                    <span 
                      className="testing-info-dot" 
                      style={{ background: colorThemes.find(t => t.id === testTheme)?.preview }}
                    />
                    {colorThemes.find(t => t.id === testTheme)?.name}
                  </span>
                </div>
                <div className="testing-info-note testing-info-note-cta">
                  Click the button to see your selections applied 👉
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* API Key Generator - Ready to Integrate */}
        <div className="api-key-section">
          <ApiKeyGenerator productType="widget" />
        </div>
      </div>
      
      {/* Empty container for styling purposes */}
      <div className="App">
      </div>

      {/* Widget Component */}
      <LGBTResourcesWidget 
        apiKey={apiKey} 
        theme={testTheme}
        youthSafe={testYouthSafe}
        key={`${testTheme}-${testYouthSafe}`}
      />
    </>
  );
}

export default WidgetDemo;